"use client";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Minus,
  Plus,
} from "lucide-react";

/**
 * Landing-page FAQ section.
 *
 * Source of truth is `/api/faqs` — same data the admin manages at
 * `/admin/content` → "FAQ" tab. Only entries with `isActive: true` come back.
 * On a fresh database the public endpoint seeds default FAQs so the home page
 * is never empty for new tenants. Run `npm run seed:faqs` to replace existing DB entries.
 */

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07 }}
      className="overflow-hidden border-b border-border"
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-center justify-between gap-4 px-1 py-5 text-left"
      >
        <span
          className={`font-display flex-1 text-[15px] font-semibold transition-colors ${
            open ? "text-accent-amber" : "text-text-primary"
          }`}
        >
          {item.question}
        </span>
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-all ${
            open
              ? "border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.15)]"
              : "border-border bg-[rgba(255,255,255,0.05)]"
          }`}
        >
          {open ? (
            <Minus size={14} color="var(--accent-amber)" />
          ) : (
            <Plus size={14} color="var(--text-muted)" />
          )}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="whitespace-pre-line pb-5 pr-11 text-sm leading-7 text-text-muted">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  const [faqs, setFaqs] = useState(null);
  const [activeMobileIndex, setActiveMobileIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/faqs");
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setFaqs(Array.isArray(data?.faqs) ? data.faqs : []);
      } catch {
        if (!cancelled) setFaqs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const list = faqs || [];
  const safeIndex = list.length === 0 ? 0 : Math.min(activeMobileIndex, list.length - 1);

  const nextFAQ = () => {
    if (!list.length) return;
    setActiveMobileIndex((prev) => (prev + 1) % list.length);
  };

  const prevFAQ = () => {
    if (!list.length) return;
    setActiveMobileIndex((prev) => (prev - 1 + list.length) % list.length);
  };

  const loading = faqs === null;
  const empty = !loading && list.length === 0;

  return (
    <section
      id="faq"
      className="bg-bg-secondary px-4 py-16 sm:px-6 sm:py-24 xl:py-32"
    >
      <div className="mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 text-center sm:mb-16"
        >
          <span className="mb-4 inline-block rounded-full border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.1)] px-4 py-1.5 text-xs font-medium text-accent-amber sm:text-sm">
            FAQ
          </span>
          <h2 className="font-display mb-3 text-[clamp(28px,5vw,52px)] leading-tight text-text-primary">
            Frequently Asked <span className="text-ember">Questions</span>
          </h2>
          <p className="text-sm text-text-muted sm:text-base">
            Everything You Need to Know About MineMe
          </p>
        </motion.div>

        {loading ? (
          <div className="card-base flex min-h-[200px] flex-col items-center justify-center gap-3 px-7 py-10 text-text-muted">
            <Loader2 className="h-7 w-7 animate-spin text-accent-amber" aria-hidden />
            <p className="text-sm">Loading questions…</p>
          </div>
        ) : empty ? (
          <div className="card-base flex min-h-[200px] flex-col items-center justify-center gap-2 px-7 py-10 text-text-muted">
            <p className="text-sm">No questions are available right now.</p>
          </div>
        ) : (
          <>
            {/* Desktop View: Full List */}
            <div className="card-base hidden px-7 py-2 sm:block">
              {list.map((item, i) => (
                <FAQItem key={item.id || i} item={item} index={i} />
              ))}
            </div>

            {/* Mobile View: Single FAQ Carousel */}
            <div className="sm:hidden">
              <div className="card-base relative mb-6 min-h-[280px] overflow-hidden p-4 2xl:p-6 xl:p-6 lg:p-5 md:p-5 sm:p-4">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={list[safeIndex]?.id || safeIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="flex h-full flex-col"
                  >
                    <div className="font-display mb-4 text-sm font-bold text-accent-amber">
                      Question {safeIndex + 1}
                    </div>
                    <h3 className="font-display mb-4 text-lg font-bold leading-snug text-text-primary">
                      {list[safeIndex]?.question}
                    </h3>
                    <p className="whitespace-pre-line text-sm leading-relaxed text-text-muted">
                      {list[safeIndex]?.answer}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="flex items-center justify-between px-2">
                <div className="text-xs font-medium text-text-muted">
                  <span className="text-text-primary">{safeIndex + 1}</span> of {list.length}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={prevFAQ}
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-white/5 text-text-muted transition-all active:scale-95"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextFAQ}
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-accent-amber/30 bg-accent-amber/10 text-accent-amber shadow-lg shadow-accent-amber/10 transition-all active:scale-95"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
